import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const TOKEN_KEY = "aura_token";
const ADMIN_TOKEN_KEY = "aura_admin_token";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadUser = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const startSession = (data) => {
    localStorage.setItem(TOKEN_KEY, data.token);
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    setUser(data.user);
    return data.user;
  };

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    return startSession(data);
  };

  const refreshUser = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setUser(null);
      return null;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      setUser(null);
      return null;
    }
  }, []);

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    return data;
  };

  const impersonate = async (userId) => {
    const current = localStorage.getItem(TOKEN_KEY);
    const { data } = await api.post(`/admin/users/${userId}/impersonate`);
    if (current && !localStorage.getItem(ADMIN_TOKEN_KEY)) {
      localStorage.setItem(ADMIN_TOKEN_KEY, current);
    }
    localStorage.setItem(TOKEN_KEY, data.token);
    setUser(data.user);
    return data.user;
  };

  const stopImpersonating = async () => {
    const adminTok = localStorage.getItem(ADMIN_TOKEN_KEY);
    if (!adminTok) return null;
    localStorage.setItem(TOKEN_KEY, adminTok);
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    const { data } = await api.get("/auth/me");
    setUser(data);
    return data;
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    setUser(null);
  };

  const updateProfile = async (payload) => {
    const { data } = await api.put("/profile", payload);
    setUser((u) => ({ ...u, profile: data.profile }));
    return data.profile;
  };

  const saveOnboarding = async (payload) => {
    const { data } = await api.put("/seller/onboarding", payload);
    setUser((u) => (u ? { ...u, profile: data.profile || { ...(u.profile || {}), onboarding: data.onboarding } } : u));
    return data;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, startSession, logout, updateProfile, saveOnboarding, impersonate, stopImpersonating, apiError, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}
